import React, { useEffect, useMemo, useState } from 'react';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../lib/supabase';
import { effectiveDerived } from '../../config/dealPacketForms';
import { loadBhphAccounts } from '../../services/bhphAccounts';

const money = (v) =>
  Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dateStr = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const daysSince = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / MS_PER_DAY) : null);

const REPORTS = [
  { id: 'aging', label: 'Inventory Aging', group: 'Inventory' },
  { id: 'lot-eval', label: 'Cars on Lot Evaluation', group: 'Inventory' },
  { id: 'upcoming', label: 'Upcoming Payments (14 days)', group: 'BHPH' },
  { id: 'late', label: 'Late Accounts', group: 'BHPH' },
  { id: 'sales-tax', label: 'Sales Tax Summary', group: 'Sales' },
];

function downloadCsv(filename, header, rows) {
  const esc = (v) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const Table = ({ header, rows, emptyText }) => (
  <div className="overflow-x-auto bg-white rounded-xl shadow">
    {rows.length === 0 ? (
      <p className="p-6 text-sm text-gray-500 text-center">{emptyText}</p>
    ) : (
      <table className="w-full text-sm">
        <thead className="bg-gray-100 text-gray-700">
          <tr>
            {header.map((h, i) => (
              <th key={h} className={`px-3 py-2 ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={`border-b ${i % 2 ? 'bg-gray-50' : ''}`}>
              {r.map((c, j) => (
                <td key={j} className={`px-3 py-1.5 ${j === 0 ? 'text-left' : 'text-right'}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

// Canned reports over live Supabase data — the handful a small BHPH lot pulls
// weekly, modeled on DeskManager's report library.
const ReportsHub = () => {
  const [report, setReport] = useState('aging');
  const [vehicles, setVehicles] = useState([]);
  const [deals, setDeals] = useState([]);
  const [bhph, setBhph] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [vehRes, dealRes, accounts] = await Promise.all([
        supabase.from('vehicles_db2025').select('*'),
        supabase.from('deals').select('*').order('created_at', { ascending: false }),
        loadBhphAccounts().catch(() => []),
      ]);
      if (vehRes.error) throw vehRes.error;
      if (dealRes.error) throw dealRes.error;
      setVehicles(vehRes.data || []);
      setDeals(dealRes.data || []);
      setBhph(accounts);
    } catch (err) {
      console.error('Report load failed:', err);
      setError(err.message || 'Failed to load report data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const built = useMemo(() => {
    const onLot = vehicles.filter((v) => v.status !== 'sold');

    if (report === 'aging') {
      const rows = onLot
        .map((v) => ({ v, days: daysSince(v.created_at) }))
        .sort((a, b) => (b.days ?? -1) - (a.days ?? -1));
      const bucket = (lo, hi) =>
        rows.filter((r) => r.days !== null && r.days >= lo && (hi === null || r.days <= hi)).length;
      return {
        header: ['Vehicle', 'Stock #', 'VIN', 'Price', 'Days on lot'],
        rows: rows.map(({ v, days }) => [
          `${v.year} ${v.make} ${v.model}`, v.stock_number || '—', v.vin || '—',
          `$${money(v.price)}`, days ?? '—',
        ]),
        summary: `0–30 days: ${bucket(0, 30)} · 31–60: ${bucket(31, 60)} · 61–90: ${bucket(61, 90)} · 90+: ${bucket(91, null)}`,
        emptyText: 'No unsold inventory.',
      };
    }

    if (report === 'lot-eval') {
      const byMake = new Map();
      onLot.forEach((v) => {
        const key = (v.make || 'UNKNOWN').trim().toUpperCase() || 'UNKNOWN';
        if (!byMake.has(key)) byMake.set(key, { count: 0, days: [], price: 0 });
        const g = byMake.get(key);
        g.count += 1;
        const d = daysSince(v.created_at);
        if (d !== null) g.days.push(d);
        g.price += Number(v.price || 0);
      });
      const rows = [...byMake.entries()]
        .sort((a, b) => b[1].count - a[1].count)
        .map(([make, g]) => [
          make, g.count,
          g.days.length ? Math.round(g.days.reduce((s, x) => s + x, 0) / g.days.length) : '—',
          `$${money(g.count ? g.price / g.count : 0)}`,
          `$${money(g.price)}`,
        ]);
      return {
        header: ['Make', 'On lot', 'Avg days', 'Avg price', 'Total value'],
        rows,
        summary: `${onLot.length} vehicles on the lot · total asking value $${money(onLot.reduce((s, v) => s + Number(v.price || 0), 0))}`,
        emptyText: 'No unsold inventory.',
      };
    }

    if (report === 'upcoming') {
      const horizon = new Date(Date.now() + 14 * MS_PER_DAY);
      const rows = bhph
        .filter((a) => a.hasSchedule && a.nextDueDate && a.nextDueDate <= horizon && a.daysLate === 0)
        .sort((a, b) => a.nextDueDate - b.nextDueDate)
        .map((a) => [
          a.deal.customer_name || '—',
          a.vehicle ? `${a.vehicle.year} ${a.vehicle.make} ${a.vehicle.model}` : '—',
          dateStr(a.nextDueDate), `$${money(a.paymentAmount)}`,
          a.deal.customer_phone || a.deal.customer_email || '—',
        ]);
      return {
        header: ['Buyer', 'Vehicle', 'Due date', 'Payment', 'Contact'],
        rows,
        summary: `${rows.length} payments expected in the next 14 days`,
        emptyText: 'No BHPH payments due in the next 14 days.',
      };
    }

    if (report === 'late') {
      const rows = bhph
        .filter((a) => a.daysLate > 0)
        .sort((a, b) => b.daysLate - a.daysLate)
        .map((a) => [
          a.deal.customer_name || '—',
          a.vehicle ? `${a.vehicle.year} ${a.vehicle.make} ${a.vehicle.model}` : '—',
          a.daysLate, `$${money(a.dueAmount)}`, `$${money(a.principalBalance)}`,
          a.deal.customer_phone || a.deal.customer_email || '—',
        ]);
      return {
        header: ['Buyer', 'Vehicle', 'Days late', 'Due amount', 'Principal bal.', 'Contact'],
        rows,
        summary: `${rows.length} accounts behind (website payments only — office cash payments aren't counted)`,
        emptyText: 'No late BHPH accounts. 🎉',
      };
    }

    // sales-tax
    const withModel = deals.filter((d) => d.forms_data && d.forms_data.money);
    const byMonth = new Map();
    withModel.forEach((d) => {
      const month = String(d.created_at || '').slice(0, 7) || 'unknown';
      if (!byMonth.has(month)) byMonth.set(month, { count: 0, tax: 0, sales: 0 });
      const g = byMonth.get(month);
      const derived = effectiveDerived(d.forms_data);
      g.count += 1;
      g.tax += Number(derived.salesTax || 0);
      g.sales += Number(d.total_price || 0);
    });
    const rows = [...byMonth.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([month, g]) => [month, g.count, `$${money(g.sales)}`, `$${money(g.tax)}`]);
    const skipped = deals.length - withModel.length;
    return {
      header: ['Month', 'Deals', 'Total sales', 'Sales tax'],
      rows,
      summary: `Motor-vehicle sales tax computed from each deal's saved forms model${skipped > 0 ? ` · ${skipped} older deals without forms data are not included` : ''}`,
      emptyText: 'No deals with saved forms data yet.',
    };
  }, [report, vehicles, deals, bhph]);

  const active = REPORTS.find((r) => r.id === report);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-600">Live numbers from inventory, deals, and BHPH accounts.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() =>
              downloadCsv(
                `${report}-${new Date().toISOString().slice(0, 10)}.csv`,
                built.header,
                built.rows
              )
            }
            disabled={loading || built.rows.length === 0}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
          >
            <SafeIcon icon={FiDownload} className="w-4 h-4" /> Export CSV
          </button>
          <button
            type="button"
            onClick={refresh}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Report picker */}
      <div className="flex flex-wrap gap-2">
        {REPORTS.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => setReport(r.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              report === r.id
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {r.group}: {r.label}
          </button>
        ))}
      </div>

      {error && <div className="bg-red-50 text-red-800 text-sm rounded-lg px-4 py-2">{error}</div>}

      {loading ? (
        <p className="text-sm text-gray-500 py-8 text-center">Loading {active?.label}…</p>
      ) : (
        <>
          <p className="text-sm text-gray-600">{built.summary}</p>
          <Table header={built.header} rows={built.rows} emptyText={built.emptyText} />
        </>
      )}
    </div>
  );
};

export default ReportsHub;
